<script setup>
import { computed, watch, onMounted, ref } from "vue";
import { useGtag } from "vue-gtag-next";

import AppFormInput from "@/components/Form/AppFormInput";
import AppAlertCard from "@/components/AppAlertCard";
import AppButton from "@/components/AppButton";
import AppCard from "@/components/AppCard";
import AppAlert from "@/components/AppAlert";
import { store } from "@/store";

import useApi from "@/composables/useApi";

const { user } = useApi();

const { event } = useGtag();

const formSent = ref(false);
const pass1 = ref("");
const pass2 = ref("");
const input1 = ref(null);
const input2 = ref(null);
const loading = ref(false);

const is_error = ref(false);

const profile = computed(() => store.user);

const isValid = computed(() => {
  return pass1.value === pass2.value;
});

// Visual counterpart of the native validity set below
const showMismatch = computed(() => pass2.value.length > 0 && !isValid.value);

const submitForm = async (e) => {
  try {
    loading.value = true;
    const formDataObject = {};
    const formData = new FormData();
    const body = new FormData(e.target);

    body.forEach((value, key) => (formDataObject[key] = value));

    // formDataObject.current_password = encrypt(formDataObject.current_password);
    // formDataObject.new_password = encrypt(formDataObject.new_password);
    // formDataObject.confirm_password = encrypt(formDataObject.confirm_password);

    for (var key in formDataObject) {
      formData.append(key, formDataObject[key]);
    }

    const result = await user.changePassword(
      profile.value.id,
      formDataObject.current_password,
      formDataObject.new_password,
      formDataObject.confirm_password
    );
    // console.log(result);
    if (result) {
      is_error.value = false;
      formSent.value = true;
      event("password_changed", {
        event_category: "password_changed",
        event_label: "success",
      });
    } else {
      throw new Error("Failed to change password");
    }
  } catch (error) {
    is_error.value = true;
    event("password_changed", {
      event_category: "password_changed",
      event_label: "error",
    });
  } finally {
    loading.value = false;
  }
};

watch(isValid, (isValid) => {
  let i1 = input1.value.$refs.refInput.textInput;
  let i2 = input2.value.$refs.refInput.textInput;
  i1.setCustomValidity(isValid ? "" : "hey");
  i2.setCustomValidity(isValid ? "" : "hey");
});

onMounted(async () => {
  store.APP_LOADING = false;
});
</script>

<template lang="pug">
.change-pass
  AppCard(v-if="!formSent" padding="lg" elevated)
    .change-pass__header
      span.change-pass__badge
        i.uil.uil-shield-check(aria-hidden="true")
      .change-pass__heading
        h2 {{ $t("changePass.card_title") }}
        p {{ $t("changePass.card_subtitle") }}
    transition(name="alert")
      AppAlert.change-pass__alert(
        :title="$t('changePass.error_title')"
        :subtitle="$t('changePass.error_subtitle')"
        v-if="is_error"
      )
    form(@submit.prevent="submitForm")
      AppFormInput(
        type="password"
        name="current_password"
        icon="uil uil-lock-alt"
        :label="$t('changePass.current')"
        required
      )
      .change-pass__divider
      AppFormInput(
        type="password"
        ref="input1"
        name="new_password"
        icon="uil uil-key-skeleton"
        :label="$t('changePass.new')"
        @input="pass1 = $event.target.value"
        required
      )
      AppFormInput(
        type="password"
        ref="input2"
        name="confirm_password"
        icon="uil uil-key-skeleton"
        :label="$t('changePass.confirm')"
        @input="pass2 = $event.target.value"
        required
      )
      p.change-pass__mismatch(v-if="showMismatch")
        i.uil.uil-exclamation-circle(aria-hidden="true")
        span {{ $t("changePass.mismatch") }}
      AppButton(:loading="loading" :disabled="loading" :label="$t('common.send')")
  AppAlertCard(:title="$t('changePass.success_title')" :subtitle="$t('changePass.success_subtitle')" v-else)
</template>

<style lang="scss" scoped>
.change-pass {
  @apply pb-6;
  @apply w-full;
  @apply max-w-lg;
  // Full width on phones, centered column from there on
  @apply mx-auto;

  &__header {
    @apply flex;
    @apply items-start;
    @apply gap-3;
    @apply mb-6;
  }

  &__badge {
    @apply flex;
    @apply flex-shrink-0;
    @apply items-center;
    @apply justify-center;
    @apply w-11;
    @apply h-11;
    @apply rounded-xl;
    @apply bg-gignetgreen-200;

    i {
      @apply text-2xl;
      @apply leading-none;
      @apply text-gignetgreen-700;
    }
  }

  &__heading {
    @apply flex-1;
    @apply min-w-0;

    h2 {
      @apply font-semibold;
      @apply text-bluegray-700;
      @apply leading-snug;
    }

    p {
      @apply text-sm;
      @apply text-bluegray-400;
      @apply leading-snug;
      @apply mt-1;
    }
  }

  &__alert {
    @apply mb-5;
  }

  &__divider {
    @apply h-px;
    @apply w-full;
    @apply bg-bluegray-100;
    @apply mb-5;
  }

  &__mismatch {
    @apply flex;
    @apply items-center;
    @apply gap-1.5;
    @apply -mt-2;
    @apply mb-4;
    @apply text-xs;
    @apply font-medium;
    @apply text-red-500;

    i {
      @apply text-base;
      @apply leading-none;
    }
  }
}

.alert-enter-active,
.alert-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.alert-enter-from,
.alert-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
